import { Download } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import * as Sentry from "@sentry/browser";
import TransitionalRegion from "components/TransitionalRegion";
import useGetSignedVideoUrls from "hooks/api/useGetSignedVideoUrls";
import useGetVideoById from "hooks/api/useGetVideoById";
import useCreateDownload from "hooks/useCreateDownload";
import useCurrentUser from "hooks/useCurrentUser";
import useGetBlurredProjects from "hooks/useGetBlurredProjects";
import useProjectId from "hooks/ProjectScope";
import useProject from "hooks/useProject";
import { useState } from "react";
import ReactPlayer, { ReactPlayerProps } from "react-player";
import { OnProgressProps } from "react-player/base";
import getVideoDownloadFilename from "utils/getVideoDownloadFilename";
import { hasViewerRole } from "utils/roles";
import useApplyPlaybackRate from "./hooks/useApplyPlaybackRate";
import useAutoLoadedSubtitleUrls from "./hooks/useAutoLoadedSubtitleUrls";
import useOnReadyHandler from "./hooks/useOnReadyHandler";
import useResetPlayerOnSourceChange from "./hooks/useResetPlayerOnSourceChange";
import { applyPlayerOverrides } from "./utils/applyPlayerOverrides";
import useGetParticipantById from "hooks/api/useGetParticipantById";

interface Props extends ReactPlayerProps {
  controls?: boolean;
  endTime?: number;
  onEnded?: () => void;
  onProgress?: (onProgressProps: OnProgressProps) => void;
  onReady?: (player: ReactPlayer | undefined) => void;
  playing?: boolean;
  progressInterval?: number;
  startTime?: number;
  videoId?: string;
  transcodeOverride?: string;
  allowDownload?: boolean;
  forceBlur?: boolean;
  /** When false, skips auto-loading subtitles. Default true. */
  allowSubtitles?: boolean;
  onPlaybackRateChange?: (rate: number) => void;
}

export default function BasicPlayer({
  controls,
  endTime,
  onEnded,
  onProgress,
  onReady,
  playing,
  progressInterval = 100,
  startTime = 0,
  videoId,
  transcodeOverride,
  allowDownload = true,
  forceBlur = false,
  allowSubtitles = true,
  playbackRate,
  onPlaybackRateChange,
  ...rest
}: Props) {
  const [player, setPlayer] = useState<ReactPlayer | undefined>();
  const [canPlay, setCanPlay] = useState(false);
  const [uncontrolledPlaybackRate, setUncontrolledPlaybackRate] = useState(1);
  const effectivePlaybackRate = playbackRate ?? uncontrolledPlaybackRate;
  const { imported } = useProject();
  const projectId = useProjectId();
  const { role } = useCurrentUser();
  const showControls = controls == undefined ? true : controls;
  const canDownload = allowDownload && !hasViewerRole(role);
  const needsDownloadMetadata = Boolean(showControls && canDownload && videoId);
  const { video } = useGetVideoById(videoId, {
    suspendRequest:
      !videoId || (Boolean(transcodeOverride) && !needsDownloadMetadata),
  });

  const { video: parentVideo } = useGetVideoById(video?.parentId, {
    suspendRequest: !needsDownloadMetadata || !video?.parentId,
  });
  const participantId = needsDownloadMetadata
    ? video?.participantId || parentVideo?.participantId
    : undefined;
  const { participant } = useGetParticipantById(participantId);

  const signedVideoUrls = useGetSignedVideoUrls(videoId || "", {
    swrOptions: { revalidateOnFocus: false },
    suspendRequest: Boolean(transcodeOverride),
  });

  const transcodeUrl = signedVideoUrls?.transcodeUrl;
  const downloadUrl = transcodeOverride || transcodeUrl;
  const useCustomDownload = Boolean(needsDownloadMetadata && downloadUrl);
  const downloadFilename = getVideoDownloadFilename(video, participant);
  const download = useCreateDownload(
    useCustomDownload ? downloadUrl : undefined,
    downloadFilename,
  );

  const shouldBlur = useGetBlurredProjects();
  const blur = forceBlur || shouldBlur;

  const isNonEnglishVideo =
    video != null &&
    video.languageCode !== "en-US" &&
    video.languageCode !== "en_US";

  const autoLoadSubtitles =
    allowSubtitles &&
    !imported &&
    isNonEnglishVideo &&
    Boolean(projectId && videoId && (transcodeUrl || transcodeOverride));

  const { sourceSubtitleUrl, englishSubtitleUrl, fileTracks } =
    useAutoLoadedSubtitleUrls(videoId, autoLoadSubtitles, video?.languageCode);

  const playerKey = `${transcodeOverride || transcodeUrl}-${sourceSubtitleUrl || ""}-${englishSubtitleUrl || ""}`;

  useResetPlayerOnSourceChange(playerKey, setPlayer, setCanPlay);
  useOnReadyHandler(player, canPlay, onReady);
  useApplyPlaybackRate(player, canPlay, effectivePlaybackRate, playerKey);

  function _onReady(nextPlayer: ReactPlayer) {
    setPlayer(nextPlayer);
    applyPlayerOverrides(nextPlayer, startTime, endTime, effectivePlaybackRate);
  }

  function _onPlaybackRateChange(rate: number) {
    if (playbackRate == null) {
      setUncontrolledPlaybackRate(rate);
    }
    onPlaybackRateChange?.(rate);
  }

  function _onProgress(onProgressProps: OnProgressProps) {
    if (!player) return;

    const currentTime = player.getCurrentTime();

    if (!canPlay) {
      setCanPlay(true);
      return;
    }

    if (endTime != undefined && currentTime >= endTime) {
      _onEnded();
    }

    onProgress && onProgress(onProgressProps);
  }

  function _onEnded() {
    if (onEnded) onEnded();
  }

  function _onError(error) {
    Sentry.captureException(error);
  }

  const downloadGutterPx = 60;

  return transcodeUrl || transcodeOverride ? (
    <TransitionalRegion showSpinner={!canPlay}>
      <Box
        component="span"
        sx={{
          display: "block",
          height: "100%",
          width: "100%",
          minHeight: 0,
          position: "relative",
          overflow: "hidden",
          "& video": {
            objectFit: "contain",
          },
          ...(useCustomDownload && {
            "& video::-webkit-media-controls-timeline": {
              width: `calc(100% - ${downloadGutterPx}px) !important`,
            },
            "& video::-webkit-media-controls-timeline-container": {
              width: `calc(100% - ${downloadGutterPx}px) !important`,
            },
          }),
        }}
      >
        <ReactPlayer
          key={playerKey}
          controls={showControls}
          config={{
            file: {
              attributes: {
                disablePictureInPicture: true,
                controlsList:
                  !canDownload ||
                  useCustomDownload ||
                  Boolean(videoId && downloadUrl)
                    ? "nodownload"
                    : "",
              },
              tracks: fileTracks,
            },
          }}
          onEnded={_onEnded}
          onReady={_onReady}
          onError={_onError}
          onProgress={_onProgress}
          onPlaybackRateChange={_onPlaybackRateChange}
          url={transcodeOverride || transcodeUrl}
          playbackRate={effectivePlaybackRate}
          playing={canPlay && playing}
          progressInterval={progressInterval}
          style={{
            display: !canPlay ? "none" : "flex",
            cursor: "pointer",
            flex: 1,
          }}
          height="100%"
          width="100%"
          {...rest}
        />
        {useCustomDownload && (
          <Tooltip title={`Download "${downloadFilename}"`}>
            <IconButton
              disabled={download.isDownloading}
              onClick={download.onDownload}
              size="small"
              sx={{
                backgroundColor: "transparent",
                bottom: 4,
                color: "common.white",
                height: 28,
                p: 0.5,
                position: "absolute",
                right: 10,
                width: 28,
                zIndex: 2,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                },
              }}
            >
              {download.isDownloading ? (
                <CircularProgress
                  variant="determinate"
                  value={download.downloadProgress}
                  size={18}
                  sx={{ color: "common.white" }}
                />
              ) : (
                <Download fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
        {blur && (
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 56,
              backdropFilter: "blur(8px)",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
    </TransitionalRegion>
  ) : (
    <></>
  );
}
